import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { getProjects } from '../services/api';

const Portfolio = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [allProjects, setAllProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await getProjects();
        setAllProjects(data);
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get('category') || 'all';
    setSelectedCategory(category);

    if (category === 'all') {
      setFilteredProjects(allProjects);
    } else {
      setFilteredProjects(allProjects.filter(p => p.category === category));
    }
  }, [location, allProjects]);

  const handleCategoryChange = (category) => {
    navigate(`/portfolio${category !== 'all' ? `?category=${category}` : ''}`);
  };

  const openLightbox = (project, imageIndex) => {
    setSelectedImage(project);
    setCurrentImageIndex(imageIndex);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    setCurrentImageIndex(0);
  };

  const goToNextImage = () => {
    if (selectedImage) {
      setCurrentImageIndex((prev) => 
        prev < selectedImage.images.length - 1 ? prev + 1 : 0
      );
    }
  };

  const goToPrevImage = () => {
    if (selectedImage) {
      setCurrentImageIndex((prev) => 
        prev > 0 ? prev - 1 : selectedImage.images.length - 1
      );
    }
  };

  // Get all images for masonry layout
  const allImages = filteredProjects.flatMap(project => 
    project.images.map((image, index) => ({
      ...project,
      image,
      imageIndex: index
    }))
  );

  return (
    <div className="min-h-screen bg-[#0f0f10] pt-20">
      {/* Header Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <h1 className="text-5xl md:text-6xl font-bold text-[#f5f5f5] mb-4 tracking-wider text-center">
          Our <span className="text-[#4b63eb]">Portfolio</span>
        </h1>
        <p className="text-[#a0a0a0] text-lg text-center mb-12">
          Explore our collection of architectural photography
        </p>

        {/* Category Filters */}
        <div className="flex justify-center space-x-4 mb-12">
          <button
            onClick={() => handleCategoryChange('all')}
            className={`px-6 py-3 text-sm tracking-widest uppercase font-semibold transition-all duration-300 ${
              selectedCategory === 'all'
                ? 'bg-gradient-to-r from-[#4b63eb] to-[#3b4ec9] text-[#f5f5f5]'
                : 'bg-[#1a1a1a] text-[#a0a0a0] hover:bg-[#2d3748] hover:text-[#f5f5f5]'
            }`}
          >
            All
          </button>
          <button
            onClick={() => handleCategoryChange('residential')}
            className={`px-6 py-3 text-sm tracking-widest uppercase font-semibold transition-all duration-300 ${
              selectedCategory === 'residential'
                ? 'bg-gradient-to-r from-[#4b63eb] to-[#3b4ec9] text-[#f5f5f5]'
                : 'bg-[#1a1a1a] text-[#a0a0a0] hover:bg-[#2d3748] hover:text-[#f5f5f5]'
            }`}
          >
            Residential
          </button>
          <button
            onClick={() => handleCategoryChange('commercial')}
            className={`px-6 py-3 text-sm tracking-widest uppercase font-semibold transition-all duration-300 ${
              selectedCategory === 'commercial'
                ? 'bg-gradient-to-r from-[#4b63eb] to-[#3b4ec9] text-[#f5f5f5]'
                : 'bg-[#1a1a1a] text-[#a0a0a0] hover:bg-[#2d3748] hover:text-[#f5f5f5]'
            }`}
          >
            Commercial
          </button>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-24">
          <div className="text-[#4b63eb] text-lg">Loading portfolio...</div>
        </div>
      )}

      {/* Masonry Grid */}
      {!loading && (
        <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-24">
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
          {allImages.map((item, index) => (
            <div
              key={`${item.id}-${item.imageIndex}-${index}`}
              className="break-inside-avoid group cursor-pointer"
              onClick={() => openLightbox(item, item.imageIndex)}
            >
              <div className="relative overflow-hidden bg-[#1a1a1a] transition-all duration-300 hover:shadow-xl hover:shadow-[#4b63eb]/20">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-auto transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-[#f5f5f5] hover:text-[#4b63eb] transition-colors duration-300 z-10"
            aria-label="Close lightbox"
          >
            <X size={32} />
          </button>

          <button
            onClick={goToPrevImage}
            className="absolute left-4 text-[#f5f5f5] hover:text-[#4b63eb] text-4xl font-bold transition-colors duration-300"
            aria-label="Previous image"
          >
            ‹
          </button>

          <div className="max-w-6xl w-full">
            <img
              src={selectedImage.images[currentImageIndex]}
              alt={selectedImage.title}
              className="w-full h-auto max-h-[90vh] object-contain"
            />
          </div>

          <button
            onClick={goToNextImage}
            className="absolute right-4 text-[#f5f5f5] hover:text-[#4b63eb] text-4xl font-bold transition-colors duration-300"
            aria-label="Next image"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
